import React from 'react';

import type { TextColor, TextProps, TextVariant } from './Text.types';

/**
 * Maps variant to theme typography class.
 */
const VARIANT_CLASSES: Record<TextVariant, string> = {
  'display-xl': 'text-display-xl',
  'display-lg': 'text-display-lg',
  'display-md': 'text-display-md',
  'display-sm': 'text-display-sm',
  'heading-xxl': 'text-heading-xxl',
  'heading-xl': 'text-heading-xl',
  'heading-lg': 'text-heading-lg',
  'heading-md': 'text-heading-md',
  'heading-sm': 'text-heading-sm',
  'body-xl': 'text-body-xl',
  'body-lg': 'text-body-lg',
  'body-md': 'text-body-md',
  'body-sm': 'text-body-sm',
  'label-md': 'text-label-md',
  'label-sm': 'text-label-sm',
  'label-xs': 'text-label-xs',
};

/**
 * Maps semantic color to theme color class.
 */
const COLOR_CLASSES: Record<TextColor, string> = {
  primary: 'text-neutral-50',
  secondary: 'text-neutral-400',
  muted: 'text-neutral-500',
  accent: 'text-primary-300',
  success: 'text-success-400',
  warning: 'text-warning-400',
  error: 'text-error-400',
  info: 'text-info-400',
};

/**
 * Determines font family based on variant type.
 * Display/heading use display font (Apfel Grotezk).
 * Body/label use sans font (Inter Variable).
 */
function getFontClass(variant: TextVariant): string {
  if (variant.startsWith('display') || variant.startsWith('heading')) {
    return 'font-display';
  }
  return 'font-sans';
}

/**
 * Text primitive component for consistent typography across the app.
 *
 * Encapsulates the Dō Sakura design system typography tokens,
 * automatically applying correct font family, size, line-height,
 * and letter-spacing based on the variant.
 *
 * @example
 * ```tsx
 * // Section title
 * <Text variant="heading-lg">Section Title</Text>
 *
 * // Body text with secondary color
 * <Text variant="body-md" color="secondary">Description text</Text>
 *
 * // Kicker text with accent styling
 * <Text variant="label-sm" color="accent" className="uppercase tracking-wider">
 *   Kicker Text
 * </Text>
 * ```
 */
export function Text({
  variant = 'body-md',
  color = 'primary',
  as: Component = 'p',
  className = '',
  'data-testid': testId,
  children,
}: TextProps): React.JSX.Element {
  const variantClass = VARIANT_CLASSES[variant];
  const colorClass = COLOR_CLASSES[color];
  const fontClass = getFontClass(variant);

  return (
    <Component
      className={`${fontClass} ${variantClass} ${colorClass} ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </Component>
  );
}

export default Text;
